import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiBriefcase, FiUsers, FiDollarSign, FiTrendingUp, FiMapPin, FiSearch,
  FiArrowRight, FiTarget, FiFileText, FiBookOpen, FiCreditCard, FiAward,
  FiGlobe, FiLayers, FiChevronRight, FiCornerUpLeft,
} from 'react-icons/fi';
import { loadTable, getTableData } from '../db-viewer/data/dataAccess';
import { restoreSession } from '../../lib/session';
import {
  FranchiseLevelCode, LEVEL_ORDER, LEVEL_LABEL, FranTerritory, demoTerritory,
  regionsForCountry, regionOfState, mockAreaStats, mockPotentialPostalCodes,
  mockDevelopLeads, fmtMoney, fmtNum,
  DEMO_TERRITORY_BY_CODE, DEMO_PRINCIPAL_BY_CODE, mockCaptured,
} from './franchiseeData';
import './FranchiseePage.css';

// =============================================
// Franchisee Operations
// Capability-based: a user is a franchisee if they own an active user_franchisee
// row. The page resolves the logged-in user's franchise, paints its territory,
// shows level-aware stats, sub-franchisees, and a searchable client list.
// Admins get a franchisee picker to walk the whole seeded tree (playground).
// Real client counts come from user_client_personal / user_client_business;
// area population / revenue / businesses / opportunity / postal codes are MOCK.
// =============================================

const ADMIN_ROLES = new Set(['site_owner', 'site_admin', 'site_dev']);

function resolveLevelCode(row: Record<string, any> | null, levelsData: Record<string, any>[]): FranchiseLevelCode {
  if (!row) return 'MASTER';
  if (row.level_id != null) {
    // eslint-disable-next-line eqeqeq
    const lvl = levelsData.find(l => l.level_id == row.level_id);
    if (lvl?.level_code) return lvl.level_code as FranchiseLevelCode;
  }
  const text = String(row.franchise_level || '').toLowerCase();
  const map: Record<string, FranchiseLevelCode> = {
    master: 'MASTER', worldwide: 'MASTER',
    country: 'COUNTRY', countrywide: 'COUNTRY',
    region: 'REGION', regional: 'REGION',
    state: 'STATE', statewide: 'STATE', province: 'STATE',
    county: 'COUNTY', district: 'COUNTY',
    city: 'CITY', citywide: 'CITY',
    individual: 'INDIVIDUAL', area: 'INDIVIDUAL',
  };
  return map[text] || 'MASTER';
}

function territoryFor(row: Record<string, any> | null, level: FranchiseLevelCode): FranTerritory {
  const code = row?.franchise_code as string | undefined;
  if (code && DEMO_TERRITORY_BY_CODE[code]) return DEMO_TERRITORY_BY_CODE[code];
  return demoTerritory(level);
}

// OpenStreetMap embed (no dependency, no API key). span in degrees ≈ zoom.
function osmEmbed(lat: number, lng: number, span: number): string {
  const minLng = (lng - span).toFixed(4);
  const minLat = (lat - span).toFixed(4);
  const maxLng = (lng + span).toFixed(4);
  const maxLat = (lat + span).toFixed(4);
  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLng}%2C${minLat}%2C${maxLng}%2C${maxLat}&layer=mapnik&marker=${lat}%2C${lng}`;
}

interface StateCard { code: string; name: string; }

// Order franchisees depth-first by parent link, tagging depth (for the indented picker).
function buildHierarchy(rows: Record<string, any>[]): { row: Record<string, any>; depth: number }[] {
  const out: { row: Record<string, any>; depth: number }[] = [];
  const childrenOf = (pid: any) => rows.filter(r =>
    // eslint-disable-next-line eqeqeq
    (r.parent_franchisee_id ?? null) == (pid ?? null) && r.is_deleted != 1);
  const walk = (pid: any, depth: number) => {
    for (const r of childrenOf(pid)) {
      out.push({ row: r, depth });
      walk(r.franchisee_id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

const FranchiseePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith('/admin') ? '/admin' : '/dashboard';

  const session = useMemo(() => restoreSession(), []);
  const isAdmin = session ? ADMIN_ROLES.has(session.role) : false;

  const [, setTick] = useState(0);
  const [viewedId, setViewedId] = useState<number | null>(null);
  const [clientQuery, setClientQuery] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([
      loadTable('user_franchisee'), loadTable('site_mstr_franchise_levels'),
      loadTable('site_mstr_states'), loadTable('site_mstr_countries'),
      loadTable('users'), loadTable('user_client_personal'), loadTable('user_client_business'),
    ]).then(() => { if (alive) setTick(t => t + 1); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const franchisees = getTableData('user_franchisee').filter(f => f.is_deleted != 1);
  const levelsData = getTableData('site_mstr_franchise_levels');
  const statesData = getTableData('site_mstr_states');
  const countriesData = getTableData('site_mstr_countries');
  const personalClients = getTableData('user_client_personal');
  const businessClients = getTableData('user_client_business');

  const myUserId = session ? Number(session.id) : null;
  const myFranchisee = useMemo(() => {
    // eslint-disable-next-line eqeqeq
    const mine = franchisees.find(f => f.user_id == myUserId);
    if (mine) return mine;
    return franchisees.find(f => f.parent_franchisee_id == null) || franchisees[0] || null;
  }, [franchisees, myUserId]);

  // The franchise currently being viewed (picker / drill), defaulting to my own.
  const viewedFranchisee = useMemo(() => {
    if (viewedId != null) {
      // eslint-disable-next-line eqeqeq
      const v = franchisees.find(f => f.franchisee_id == viewedId);
      if (v) return v;
    }
    return myFranchisee;
  }, [viewedId, franchisees, myFranchisee]);

  const isOwnView = viewedFranchisee && myFranchisee
    && viewedFranchisee.franchisee_id === myFranchisee.franchisee_id;

  const activeLevel = resolveLevelCode(viewedFranchisee, levelsData);
  const territory = useMemo(() => territoryFor(viewedFranchisee, activeLevel), [viewedFranchisee, activeLevel]);

  const code = viewedFranchisee?.franchise_code as string | undefined;
  const franchiseeName = viewedFranchisee?.franchise_name || 'Akountant.com Ltd';
  const principal = (code && DEMO_PRINCIPAL_BY_CODE[code])
    || (session?.firstName ? `${session.firstName} ${session.lastName || ''}`.trim() : 'Franchise Owner');

  const parentRow = useMemo(() => {
    if (!viewedFranchisee?.parent_franchisee_id) return null;
    // eslint-disable-next-line eqeqeq
    return franchisees.find(f => f.franchisee_id == viewedFranchisee.parent_franchisee_id) || null;
  }, [viewedFranchisee, franchisees]);

  const children = useMemo(() => {
    if (!viewedFranchisee) return [];
    // eslint-disable-next-line eqeqeq
    return franchisees.filter(f => f.parent_franchisee_id == viewedFranchisee.franchisee_id);
  }, [viewedFranchisee, franchisees]);

  // Real captured clients (only meaningful for your own franchise).
  const myPersonalClients = useMemo(
    // eslint-disable-next-line eqeqeq
    () => personalClients.filter(c => c.account_owner_id == viewedFranchisee?.user_id && c.is_deleted != 1),
    [personalClients, viewedFranchisee],
  );
  const myBusinessClients = useMemo(
    // eslint-disable-next-line eqeqeq
    () => businessClients.filter(c => c.account_owner_id == viewedFranchisee?.user_id && c.is_deleted != 1),
    [businessClients, viewedFranchisee],
  );

  const captured = isOwnView ? (myPersonalClients.length + myBusinessClients.length) : mockCaptured(territory.label);
  const personalCount = isOwnView ? myPersonalClients.length : Math.round(captured * 0.55);
  const businessCount = isOwnView ? myBusinessClients.length : captured - Math.round(captured * 0.55);

  const stats = useMemo(
    () => mockAreaStats(activeLevel, territory.label, captured),
    [activeLevel, territory.label, captured],
  );

  // States of the focus country (for the painted grid).
  const focusCountry = countriesData.find(c => c.name === territory.country);
  const countryStates: StateCard[] = useMemo(() => {
    if (!focusCountry) return [];
    return statesData
      // eslint-disable-next-line eqeqeq
      .filter(s => s.countries_id == focusCountry.countries_id && s.is_deleted != 1)
      .map(s => ({ code: s.state_code, name: s.state_name }));
  }, [statesData, focusCountry]);

  const ownedSet = useMemo(() => {
    if (territory.scope === 'worldwide' || territory.scope === 'country') {
      return new Set(countryStates.map(s => s.code));
    }
    return new Set(territory.ownedStateCodes || []);
  }, [territory, countryStates]);

  // Searchable client list (own view only).
  const clientRows = useMemo(() => {
    if (!isOwnView) return [];
    const rows = [
      ...myPersonalClients.map(c => ({
        id: c.client_personal_id, type: 'Personal' as const,
        name: c.display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unnamed',
        sub: c.email || c.mobile_number || '', city: c.address_city || '',
      })),
      ...myBusinessClients.map(c => ({
        id: c.client_business_id, type: 'Business' as const,
        name: c.busi_legal_name || c.busi_operating_name || 'Unnamed business',
        sub: c.email || c.business_phone || '', city: c.address_city || '',
      })),
    ];
    const q = clientQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.name.toLowerCase().includes(q) || r.sub.toLowerCase().includes(q) || r.city.toLowerCase().includes(q));
  }, [isOwnView, myPersonalClients, myBusinessClients, clientQuery]);

  const goToClients = () => navigate(`${base}/clients-accounts`);
  const hierarchy = useMemo(() => buildHierarchy(franchisees), [franchisees]);

  const showsTerritoryGrid = ['worldwide', 'country', 'region', 'state'].includes(territory.scope);
  const showsMap = ['county', 'city', 'point'].includes(territory.scope);
  const showsOpportunityPins = territory.scope === 'point';
  const showsDevelopLeads = territory.scope !== 'point' && activeLevel !== 'INDIVIDUAL';

  const developAreas: string[] = useMemo(() => {
    if (territory.scope === 'worldwide') return countriesData.slice(0, 6).map(c => c.name);
    if (territory.scope === 'country') return (REGION_NAMES[territory.country] || []).slice(0, 6);
    if (territory.scope === 'region') return (territory.ownedStateCodes || []).map(c => countryStates.find(s => s.code === c)?.name || c);
    if (territory.scope === 'state') return ['Calgary', 'Edmonton', 'Red Deer', 'Lethbridge', 'Medicine Hat', 'Grande Prairie'];
    if (territory.scope === 'county' || territory.scope === 'city') return ['Downtown', 'Northwest', 'Northeast', 'Southwest', 'Southeast'];
    return [];
  }, [territory, countriesData, countryStates]);

  const developLeads = useMemo(() => mockDevelopLeads(territory.label, developAreas), [territory.label, developAreas]);
  const potentialPostal = useMemo(() => mockPotentialPostalCodes(territory.label, territory.country), [territory.label, territory.country]);

  return (
    <div className="fr-page">
      {/* ===== Identity header ===== */}
      <div className="fr-hero">
        <div className="fr-hero-main">
          <div className="fr-hero-badge"><FiAward /></div>
          <div>
            <div className="fr-hero-corp">{franchiseeName}</div>
            <div className="fr-hero-sub">
              {principal} · <span className="fr-level-pill">{LEVEL_LABEL[activeLevel]}</span>
            </div>
            <div className="fr-hero-terr"><FiMapPin /> {territory.label}</div>
            {parentRow && (
              <div className="fr-hero-parent">
                Reports to{' '}
                <button className="fr-link" onClick={() => setViewedId(parentRow.franchisee_id)}>
                  {parentRow.franchise_name}
                </button>
              </div>
            )}
          </div>
        </div>

        {isAdmin && hierarchy.length > 1 && (
          <div className="fr-preview">
            <label>View franchise</label>
            <select
              value={viewedFranchisee?.franchisee_id ?? ''}
              onChange={e => setViewedId(Number(e.target.value))}
            >
              {hierarchy.map(({ row, depth }) => (
                <option key={row.franchisee_id} value={row.franchisee_id}>
                  {'  '.repeat(depth)}{row.franchise_name} — {LEVEL_LABEL[resolveLevelCode(row, levelsData)]}
                </option>
              ))}
            </select>
            {!isOwnView && (
              <button className="fr-preview-reset" onClick={() => setViewedId(null)}>
                <FiCornerUpLeft /> My franchise
              </button>
            )}
          </div>
        )}
      </div>

      {!isOwnView && (
        <div className="fr-preview-note">
          Viewing <strong>{franchiseeName}</strong> ({LEVEL_LABEL[activeLevel]}). Client counts shown are illustrative —
          the live client list appears on <button className="fr-link" onClick={() => setViewedId(null)}>your own franchise</button>.
        </div>
      )}

      {/* ===== What the franchise includes (DMS + Bookkeeping + subscription, NO tax filing) ===== */}
      <section className="fr-section">
        <h2 className="fr-h2">Your Akountant franchise includes</h2>
        <div className="fr-includes">
          <div className="fr-include-card">
            <FiFileText className="fr-include-ic" />
            <div className="fr-include-name">Document Management (DMS)</div>
            <div className="fr-include-desc">Capture, separate, file & retain client documents and receipts.</div>
          </div>
          <div className="fr-include-card">
            <FiBookOpen className="fr-include-ic" />
            <div className="fr-include-name">Bookkeeping</div>
            <div className="fr-include-desc">Full books for your clients — COA, processing, financial statements.</div>
          </div>
          <div className="fr-include-card">
            <FiCreditCard className="fr-include-ic" />
            <div className="fr-include-name">Business Monthly Subscription</div>
            <div className="fr-include-desc">Recurring per-business plan billed monthly to your clients.</div>
          </div>
        </div>
        <p className="fr-includes-foot">
          Tax Filing is <strong>not</strong> part of the franchise offering and is excluded here.
        </p>
      </section>

      {/* ===== Stats ===== */}
      <section className="fr-section">
        <h2 className="fr-h2">Territory at a glance</h2>
        <div className="fr-kpis">
          <Kpi icon={<FiUsers />}      label="Personal Clients"   value={fmtNum(personalCount)} real={!!isOwnView} />
          <Kpi icon={<FiBriefcase />}  label="Business Clients"   value={fmtNum(businessCount)} real={!!isOwnView} />
          <Kpi icon={<FiDollarSign />} label="Est. Annual Revenue" value={fmtMoney(stats.estRevenue)} />
          {activeLevel !== 'INDIVIDUAL' && (
            <Kpi icon={<FiLayers />} label="Sub-franchisees" value={fmtNum(children.length || stats.subFranchisees)} real={children.length > 0} />
          )}
          <Kpi icon={<FiGlobe />}      label="Area Population"    value={fmtNum(stats.population)} />
          <Kpi icon={<FiBriefcase />}  label="Businesses in Area" value={fmtNum(stats.totalBusinesses)} />
          <Kpi icon={<FiTarget />}     label="Missed Opportunity" value={fmtNum(stats.missedBusinesses)} accent />
          <Kpi icon={<FiTrendingUp />} label="Market Captured"    value={`${stats.capturePct}%`} />
        </div>
        <p className="fr-mock-note">
          Client counts on your own franchise are live. Revenue, population, business totals,
          and opportunity are <strong>illustrative estimates</strong> until those data feeds exist.
        </p>
      </section>

      {/* ===== Sub-franchisees (drill down) ===== */}
      {children.length > 0 && (
        <section className="fr-section">
          <h2 className="fr-h2"><FiLayers /> Sub-franchisees ({children.length})</h2>
          <ul className="fr-sub-list">
            {children.map(ch => (
              <li key={ch.franchisee_id} className="fr-sub-row" onClick={() => setViewedId(ch.franchisee_id)}>
                <span className="fr-sub-name">{ch.franchise_name}</span>
                <span className="fr-sub-level">{LEVEL_LABEL[resolveLevelCode(ch, levelsData)]}</span>
                <span className="fr-sub-terr">{(ch.franchise_code && DEMO_TERRITORY_BY_CODE[ch.franchise_code]?.label) || ch.territory_note || ''}</span>
                <span className="fr-client-go">Open <FiChevronRight /></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ===== Territory paint (grid) ===== */}
      {showsTerritoryGrid && (
        <section className="fr-section">
          <h2 className="fr-h2">
            Franchise territory
            <span className="fr-legend">
              <span className="fr-swatch owned" /> Your rights
              <span className="fr-swatch open" /> Open / unassigned
            </span>
          </h2>
          {territory.scope === 'worldwide' ? (
            <div className="fr-world">
              <div className="fr-world-banner"><FiGlobe /> Worldwide master rights — every country is yours to sub-franchise.</div>
              <div className="fr-region-grid">
                {countriesData.slice(0, 12).map(c => (
                  <div key={c.countries_id} className="fr-state owned">
                    <span className="fr-state-emoji">{c.emoji || '🌐'}</span>{c.name}
                  </div>
                ))}
              </div>
              <p className="fr-mock-note">Showing the first seeded countries — drill into a country to paint its provinces & regions.</p>
            </div>
          ) : (
            <TerritoryGrid
              country={territory.country}
              states={countryStates}
              ownedSet={ownedSet}
              regionFilter={territory.scope === 'region' ? territory.regionName : undefined}
            />
          )}
        </section>
      )}

      {/* ===== Map (county/city/individual) ===== */}
      {showsMap && territory.lat != null && territory.lng != null && (
        <section className="fr-section">
          <h2 className="fr-h2">
            {territory.scope === 'point' ? 'Your business location & service area' : 'Territory map'}
          </h2>
          <div className="fr-map-wrap">
            <iframe
              title="territory-map"
              className="fr-map"
              src={osmEmbed(territory.lat, territory.lng, territory.scope === 'point' ? 0.12 : 0.28)}
              loading="lazy"
            />
            <div className="fr-map-cap">
              <FiMapPin /> {territory.label}
              {territory.scope === 'point' && ' — ~10 km service radius (zoomed out)'}
            </div>
          </div>
        </section>
      )}

      {/* ===== Opportunity: potential postal codes (individual) ===== */}
      {showsOpportunityPins && (
        <section className="fr-section">
          <h2 className="fr-h2"><FiTarget /> Potential opportunity — postal codes within ~10 km</h2>
          <div className="fr-postal-grid">
            {potentialPostal.map(pc => (<span key={pc} className="fr-postal">{pc}</span>))}
          </div>
          <p className="fr-mock-note">Illustrative postal codes — wire to <code>site_mstr_postal_codes</code> + lat/long radius when populated.</p>
        </section>
      )}

      {/* ===== Opportunity: develop new franchisees ===== */}
      {showsDevelopLeads && developLeads.length > 0 && (
        <section className="fr-section">
          <h2 className="fr-h2"><FiTrendingUp /> Develop new franchisees here</h2>
          <div className="fr-leads">
            {developLeads.map(l => (
              <div key={l.area} className="fr-lead">
                <div className="fr-lead-area">{l.area}</div>
                <div className="fr-lead-meta">
                  <span>{fmtNum(l.businesses)} businesses</span>
                  <span>{fmtNum(l.population)} people</span>
                </div>
                <button className="fr-lead-cta">Flag as prospect</button>
              </div>
            ))}
          </div>
          <p className="fr-mock-note">Illustrative sub-territories — real figures come from the geo + business data feeds.</p>
        </section>
      )}

      {/* ===== Clients list ===== */}
      <section className="fr-section">
        <div className="fr-clients-head">
          <h2 className="fr-h2">Your clients ({clientRows.length})</h2>
          {isOwnView && (
            <div className="fr-search">
              <FiSearch />
              <input placeholder="Search clients…" value={clientQuery} onChange={e => setClientQuery(e.target.value)} />
            </div>
          )}
        </div>
        {!isOwnView ? (
          <div className="fr-empty">
            Client lists are private to each franchisee. <button className="fr-link" onClick={() => setViewedId(null)}>Switch to your franchise</button> to see yours.
          </div>
        ) : clientRows.length === 0 ? (
          <div className="fr-empty">
            No clients yet. <button className="fr-link" onClick={goToClients}>Open Client's Accounts</button> to add your first.
          </div>
        ) : (
          <ul className="fr-client-list">
            {clientRows.map(r => (
              <li key={`${r.type}-${r.id}`} className="fr-client-row" onClick={goToClients}>
                <span className={`fr-client-type ${r.type.toLowerCase()}`}>{r.type}</span>
                <span className="fr-client-name">{r.name}</span>
                <span className="fr-client-sub">{r.sub}</span>
                <span className="fr-client-city">{r.city}</span>
                <span className="fr-client-go">Open in Client's Accounts <FiArrowRight /></span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

const REGION_NAMES: Record<string, string[]> = {
  Canada: ['Atlantic (Maritimes)', 'Central Canada', 'Prairies', 'West Coast', 'Northern'],
  'United States': ['Northeast', 'Midwest', 'South', 'West'],
  India: ['North India', 'West India', 'South India', 'East India', 'Central India', 'Northeast India'],
};

const Kpi: React.FC<{ icon: React.ReactNode; label: string; value: string; real?: boolean; accent?: boolean }> =
  ({ icon, label, value, real, accent }) => (
    <div className={`fr-kpi${accent ? ' accent' : ''}`}>
      <div className="fr-kpi-ic">{icon}</div>
      <div className="fr-kpi-body">
        <div className="fr-kpi-value">{value}</div>
        <div className="fr-kpi-label">
          {label}
          {real ? <span className="fr-tag live">live</span> : <span className="fr-tag est">est.</span>}
        </div>
      </div>
    </div>
  );

const TerritoryGrid: React.FC<{
  country: string; states: StateCard[]; ownedSet: Set<string>; regionFilter?: string;
}> = ({ country, states, ownedSet, regionFilter }) => {
  const allCodes = states.map(s => s.code);
  const groups = regionsForCountry(country, allCodes);
  const byCode = new Map(states.map(s => [s.code, s.name]));

  return (
    <div className="fr-regions">
      {groups
        .filter(g => !regionFilter || g.name === regionFilter)
        .map(group => {
          const groupOwned = group.stateCodes.filter(c => ownedSet.has(c)).length;
          return (
            <div key={group.name} className="fr-region">
              <div className="fr-region-title">
                {group.name}
                <span className="fr-region-count">{groupOwned}/{group.stateCodes.length}</span>
              </div>
              <div className="fr-region-grid">
                {group.stateCodes.map(c => {
                  const name = byCode.get(c) || c;
                  const owned = ownedSet.has(c);
                  return (
                    <div key={c} className={`fr-state${owned ? ' owned' : ' open'}`} title={`${name} (${regionOfState(country, c)})`}>
                      <span className="fr-state-code">{c}</span>{name}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default FranchiseePage;
